import React, { useMemo } from "react";
import { ChartWrapper, LineChart } from "components/charts";
import {
  getFormattedTimeStringFromPeriod,
  validLineData,
  createDateRange,
  type PerformanceWithAccountFilter,
  noData,
} from "features/Library/Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import { CHART_PERIOD, useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";
import { sharedAccountReportingLineProps } from "features/Reporting/Content/shared";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { useQuery } from "@tanstack/react-query";
import { getTemplateEnrollmentLineChartQuery, getTemplateEnrollmentCardDataQuery } from "./queries";
import styles from "./templateEnrollmentChart.module.scss";
import CardReporting from "components/cards/CardReporting/CardReporting";

type Props = {
  templateId: number;
  filter: PerformanceWithAccountFilter;
};

export const TemplateEnrollmentChart: React.FC<Props> = ({ templateId, filter }) => {
  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);
  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const lineChartFilter = useMemo(() => ({ ...filter, aggregation: chartPeriod }), [filter, chartPeriod]);
  const templateEnrollmentLineChartQuery = useQuery({
    queryKey: ["templateEnrollmentLineChart", templateId, lineChartFilter],
    queryFn: getTemplateEnrollmentLineChartQuery,
    enabled: chartPeriod !== CHART_PERIOD.UNSET && lineChartFilter.type !== undefined,
  });

  const templateEnrollmentCardData = useQuery({
    queryKey: ["templateEnrollmentCardData", templateId, filter],
    queryFn: getTemplateEnrollmentCardDataQuery,
    enabled: filter.type !== undefined,
  });

  return (
    <>
      <div className={styles.activity} ref={measureRef}>
        <ChartWrapper
          titles={["User Enrollment Activity"]}
          legendLabels={["Total Enrolled", "Added", "Dropped"]}
          showLegend
          colorRange={[reportingColors.blue, reportingColors.greenDark, reportingColors.red]}
        >
          <RequestStatusRenderer state={templateEnrollmentLineChartQuery.status}>
            {templateEnrollmentLineChartQuery.isSuccess &&
            validLineData(templateEnrollmentLineChartQuery.data.lineData.yData) ? (
              <LineChart
                {...sharedAccountReportingLineProps}
                xData={templateEnrollmentLineChartQuery.data.lineData.xData}
                yData={templateEnrollmentLineChartQuery.data.lineData.yData}
                xFormatterFunc={(d) => getFormattedTimeStringFromPeriod(d, chartPeriod, dateRange)}
                colorRange={[reportingColors.blue, reportingColors.greenDark, reportingColors.red]}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>
      <div className={styles.cards}>
        <RequestStatusRenderer state={templateEnrollmentCardData.status}>
          {templateEnrollmentCardData.isSuccess && <CardReporting items={templateEnrollmentCardData.data} />}
        </RequestStatusRenderer>
      </div>
    </>
  );
};
